import {
	getBulkOperationParams,
	getCurrentSorting,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/bulk-action-utils.ts';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { downloadFileFromBlob } from '@dealstack/frontend/shared/files/downloadFile.ts';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { downloadSecuritiesExcel } from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import { SecurityBulkOperationParams } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/types.ts';
import { format } from 'date-fns';
import { formatAsOfFilterDate } from '@dealstack/frontend/shared/utils/utc.ts';

interface DownloadExcelParams extends SecurityBulkOperationParams {
	filterDate?: Date | null;
}

export const useSecurityBulkExport = () => {
	const tCapManage = useTranslationCapManage();
	const getExcelRequest = useMutation({
		mutationFn: downloadSecuritiesExcel,
	});
	const [isLoading, setIsLoading] = useState(false);

	const downloadExcel = async (params: DownloadExcelParams) => {
		setIsLoading(true);

		const operationParams = getBulkOperationParams(params);
		const dataOperationParams = params.dataOperations ?? {};
		const sortBy = getCurrentSorting(params.table, dataOperationParams);

		try {
			const payload = {
				selectedIds: operationParams.ids ?? [],
				excludedIds: operationParams.excludedIds ?? [],
				personId: operationParams.personId,
				filter: dataOperationParams.filter,
				search: dataOperationParams.search,
				sortBy,
				date: formatAsOfFilterDate(params.filterDate),
			};

			const blob = await getExcelRequest.mutateAsync(payload);
			const exportDate = params.filterDate
				? format(params.filterDate, 'yyyy-MM-dd')
				: format(new Date(), 'yyyy-MM-dd');
			downloadFileFromBlob(blob, `securities-export-${exportDate}.xlsx`);
		} catch {
			showToastV2({
				state: 'error',
				message: tCapManage('toasts.errors.failed_download_csv'),
			});
		}
		setIsLoading(false);
	};

	return {
		downloadExcel,
		isLoading: isLoading || getExcelRequest.isPending,
	};
};
